import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import Avatar from 'components/common/Avatar';
import { useAuthStore } from 'stores/auth.store';
import { PATH_LOGIN, PATH_TERMINAL_HARDWARE_SHOP } from 'routes/route.path';
import { onSignOutFirebaseSession } from 'services/firebase.service';
import useFetchUser from 'pages/Users/services/useFetchUser';
import UserDetails from 'components/pages/UserDetails';
import RegisterTerminalReader from 'components/pages/RegisterTerminalReader';

const ProfileDropdown = () => {
  const { fetchUserList, userProfile, fetchUserProfile, loadingUserProfile } = useFetchUser();
  const { clearStore, userData } = useAuthStore();
  const [showDrawer, setShowDrawer] = useState(false);
  const [showRegisterReader, setShowRegisterReader] = useState(false);
  const navigate = useNavigate();
  const { avatar, name = '' } = userData || {};

  // is_show_payout already means "supervises or owns the payment point", computed by the same
  // authorisation test the account-session endpoint enforces. The hardware shop is only offered in
  // the markets that have a Stripe platform account behind it.
  const canShopTerminalHardware =
    !!userData?.isShowPayout && ['AU', 'US', 'GB'].includes(userData?.stripeOnboardingBasedCountry);

  const onLogOut = () => {
    clearStore(() => navigate(PATH_LOGIN));
    onSignOutFirebaseSession();
  };

  const onShowProfile = () => {
    setShowDrawer(true);
    fetchUserProfile();
  };

  const onCloseDrawer = () => {
    setShowDrawer(false);
  };

  return (
    <>
      <Dropdown navbar={true} as="li">
        <Dropdown.Toggle
          bsPrefix="toggle"
          as={Link}
          to="#!"
          className="pe-0 ps-2 nav-link justify-content-center align-items-center d-flex"
        >
          {avatar ? <Avatar src={avatar} /> : <Avatar size="xl" name={name} className="me-2" />}
          <span className="ps-2 text-truncate max-w-100 text-900">
            {(name || '').split(' ')[0] || 'Profile'}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu className="dropdown-caret dropdown-menu-card dropdown-menu-end">
          <div className="bg-white rounded-2 py-2 dark__bg-1000">
            <Dropdown.Item as="span" onClick={onShowProfile} className="cursor-pointer">
              Profile settings
            </Dropdown.Item>
            {canShopTerminalHardware && (
              <>
                <Dropdown.Item as={Link} to={PATH_TERMINAL_HARDWARE_SHOP}>
                  Terminal Hardware Shop
                </Dropdown.Item>
                {/* A drawer rather than a page: registering a reader is a single two-field action, and
                    whoever can order hardware is exactly who needs to register what they ordered. */}
                <Dropdown.Item
                  as="span"
                  onClick={() => setShowRegisterReader(true)}
                  className="cursor-pointer"
                >
                  Register Terminal Reader
                </Dropdown.Item>
              </>
            )}
            <Dropdown.Item
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none text-900 d-block"
              href={process.env.REACT_APP_SUPPORT_REQUEST_LINK}
            >
              Help
            </Dropdown.Item>
            <Dropdown.Item onClick={onLogOut}>Logout</Dropdown.Item>
          </div>
        </Dropdown.Menu>
      </Dropdown>
      {showDrawer && (
        <UserDetails
          show={showDrawer}
          onHide={onCloseDrawer}
          data={userProfile}
          loading={loadingUserProfile}
          fetchData={fetchUserProfile}
          fetchUserList={fetchUserList}
        />
      )}
      {/* Mounted only while open so the form and the last registered reader reset on close. */}
      {showRegisterReader && (
        <RegisterTerminalReader
          show={showRegisterReader}
          onHide={() => setShowRegisterReader(false)}
        />
      )}
    </>
  );
};

export default ProfileDropdown;
